<template>
  <div class="el-layout-tree">
    <div v-if="props.noSearchInput" class="el-layout-tree-search">
      <el-input
        v-model="filterText"
        class="input-with-select"
        :placeholder="props.filterPlaceholder"
        :clearable="props.clearable"
        @input="handleInput"
        @keyup.enter="submit"
      >
        <template #append>
          <el-button :icon="Search" @click="submit" />
        </template>
      </el-input>
      <template v-if="props.showExpandIcon">
        <el-icon
          class="el-layout-tree-search-more fold-btn"
          v-show="expandAllStatus"
          @click="handleExpand"
          title="展开"
        >
          <DArrowRight />
        </el-icon>
        <el-icon
          class="el-layout-tree-search-more expand-btn"
          v-show="!expandAllStatus"
          @click="handleExpand"
          title="折叠"
        >
          <DArrowRight />
        </el-icon>
      </template>
    </div>
    <div ref="elLayoutTreeBody" class="el-layout-tree-body">
      <el-scrollbar class="pe-layout-tree-lazy" style="overflow-x: hidden">
        <template v-if="props.lazy">
          <el-tree
            ref="tree"
            v-loading="treeLoading"
            :data="dataListData"
            element-loading-text="正在加载中..."
            :props="defaultProps"
            :node-key="props.nodeKey"
            :show-checkbox="props.showCheckbox"
            :expand-on-click-node="props.expandOnClickNode"
            :lazy="props.lazy"
            :lazy-key="props.lazyKey"
            :load="loadNode"
            :filter-node-method="filterMethod"
            @node-click="handleNodeClick"
          >
            <template #default="{ data }">
              <slot :data="data">
                <template v-if="props.icon">
                  <el-icon class="layout-tree-icon" :color="props.iconColor">
                    <component :is="props.icon" />
                  </el-icon>
                </template>
                <template v-else>
                  <el-icon
                    v-if="data.children?.length > 0"
                    class="layout-tree-icon"
                  >
                    <FolderOpened />
                  </el-icon>
                  <el-icon v-else class="layout-tree-icon">
                    <Document />
                  </el-icon>
                </template>
                <template v-if="props.isTooltip">
                  <el-tooltip
                    effect="dark"
                    :show-after="500"
                    :content="data[props.label]"
                    :placement="props.placement"
                  >
                    <span class="layout-tree-text">{{
                      data[props.label]
                    }}</span>
                    <template #content>
                      <slot name="toolTip" :row="data">{{
                        data[props.label]
                      }}</slot>
                    </template>
                  </el-tooltip>
                </template>
                <template v-else>
                  <span class="layout-tree-text">{{ data[props.label] }}</span>
                  <slot name="right" :data="data" />
                </template>
              </slot>
            </template>
          </el-tree>
        </template>
        <template v-else>
          <el-tree-v2
            ref="tree"
            v-loading="treeLoading"
            element-loading-text="正在加载中..."
            :props="defaultProps"
            :height="height"
            :filter-method="filterMethod"
            highlight-current
            :expand-on-click-node="false"
            :check-on-click-node="false"
            :item-size="getItemSize"
            :show-checkbox="props.showCheckbox"
            :default-expanded-keys="defaultExpandedKeys"
            :is-layout-tree="true"
            empty-text="暂无数据"
            @check="handleChecked"
            @node-click="handleNodeClick"
          >
            <template #default="{ data }">
              <slot :data="data">
                <template v-if="props.icon">
                  <el-icon class="layout-tree-icon" :color="props.iconColor">
                    <component :is="props.icon" />
                  </el-icon>
                </template>
                <template v-else>
                  <el-icon
                    v-if="data.children?.length > 0"
                    class="layout-tree-icon"
                  >
                    <FolderOpened />
                  </el-icon>
                  <el-icon v-else class="layout-tree-icon">
                    <Document />
                  </el-icon>
                </template>
                <template v-if="props.isTooltip">
                  <el-tooltip
                    effect="dark"
                    :show-after="500"
                    :content="data[props.label]"
                    :placement="props.placement"
                  >
                    <span class="layout-tree-text">{{
                      data[props.label]
                    }}</span>
                    <template #content>
                      <slot name="toolTip" :row="data">{{
                        data[props.label]
                      }}</slot>
                    </template>
                  </el-tooltip>
                </template>
                <template v-else>
                  <span class="layout-tree-text">{{ data[props.label] }}</span>
                  <slot name="right" :data="data" />
                </template>
              </slot>
            </template>
          </el-tree-v2>
        </template>
      </el-scrollbar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { Document, FolderOpened, Search, DArrowRight } from 'pit-element-icons'
import { flattenTree, renderTemplate } from '@pit-element-plus/utils'
import { useGlobalProperties } from '@pit-element-plus/hooks'
import { useGlobalConfig } from '@pit-element-plus/components/config-provider'
import { ElTreeV2 } from '@pit-element-plus/components'
import { layoutTreeEmits, layoutTreeProps } from './layout-tree'
import type {
  CheckedInfo,
  TreeNode,
  TreeNodeData,
} from '@pit-element-plus/components/tree-v2/src/types'
import type { LayoutTreeContext } from './types'

defineOptions({
  name: 'ElLayoutTree',
})

const props = defineProps(layoutTreeProps)
const emit = defineEmits(layoutTreeEmits)
const global = useGlobalProperties()
const isTreeShowAll = useGlobalConfig('isTreeShowAll')
const treeItemSize = useGlobalConfig('treeItemSize')

const filterText = ref('')
const dataListData: any = ref([])
const elLayoutTreeBody = ref()
const height = ref(0)
const treeLoading = ref(false)
const tree = ref<InstanceType<typeof ElTreeV2>>()
const defaultExpandedKeys: any = ref([])
const expandAllStatus: any = ref(false)

const getItemSize = computed(() => props.itemSize || treeItemSize.value)

const defaultProps = {
  children: 'children',
  label: props.label,
  isLeaf: 'isLeaf',
  value: props.nodeKey,
}

const handleInput = () => {
  if (props.noFilterInput) {
    submit()
  }
}

const submit = () => {
  if (props.beforeSearchCallback) {
    props.beforeSearchCallback()
  } else {
    submitFn()
  }
}

const submitFn = () => {
  if (props.isRequestQuery) {
    getQueryTree()
  } else {
    tree.value!.filter(filterText.value)
  }
}

const filterMethod = (value: any, data: { [x: string]: string | any[] }) => {
  if (!value) return true
  return data[props.label].includes(value)
}

const loadNode = (node: any, resolve: (data: any) => void) => {
  if (Object.keys(node.data).length > 0) {
    const params = {
      ...props.queryTreeParams,
    }
    params[props.lazyKey] = node.data[props.lazyKey]
    const url = renderTemplate(props.queryTreeInterface as string, params)
    global.$http
      .request({
        url,
        method: props.queryTreeInterfaceMethod,
        data: params,
      })
      .then((res: any) => {
        if (res['data'].length > 0) {
          res['data'].forEach((item: any) => {
            if (!item.isLeaf) {
              item.isLeaf = !item.hasChildren
            }
          })
          return resolve(res['data'])
        } else {
          return resolve([])
        }
      })
  }
}

const getQueryTree: LayoutTreeContext['getQueryTree'] = () => {
  treeLoading.value = true
  if (props.queryTreeInterface) {
    const queryTreeParams = props.queryTreeParams as object
    const url = renderTemplate(props.queryTreeInterface, queryTreeParams)
    global.$http
      .request({
        url,
        method: props.queryTreeInterfaceMethod,
        data: props.queryTreeParams,
      })
      .then((res: any) => {
        treeLoading.value = false
        let data: any = []
        if (res['data']['list']) {
          data = res['data']['list']
        } else if (res['data']) {
          data = res['data']
        }
        if (props.lazy) {
          // eslint-disable-next-line array-callback-return
          data.map((item: any) => {
            item.isLeaf = !item.hasChildren
          })
        }
        if (props.defaultObjectData) {
          // eslint-disable-next-line vue/no-mutating-props
          props.defaultObjectData[defaultProps.children] = data
          data = [props.defaultObjectData]
        } else {
          if (isTreeShowAll.value) {
            if (data && data.length > 0) {
              if (props.forceOnSelectAll) {
                const newData = [
                  {
                    [props.nodeKey]:
                      props.queryTreeParams &&
                      props.queryTreeParams[props.lazyKey]
                        ? props.queryTreeParams[props.lazyKey]
                        : '',
                    [defaultProps.label]: '全部',
                    hasChildren: true,
                    isLeaf: false,
                    [defaultProps.children]: data,
                  },
                ]
                data = newData
              }
            }
          }
        }
        setDataList(data)
        if (props.defaultExpandAll) {
          nextTick(() => {
            defaultExpandedKeys.value = getExpanedKeys(data) as never[]
            setExpandedKeysData(defaultExpandedKeys.value)
          })
        } else {
          const ids = getTreeIds2(data, props.expandedLevel)
          nextTick(() => {
            defaultExpandedKeys.value = ids
            setExpandedKeysData(defaultExpandedKeys.value)
          })
        }
        nextTick(() => {
          emit('result-back', data)
        })
      })
      .finally(() => {
        treeLoading.value = false
      })
  }
}

const handleNodeClick = (data: TreeNodeData, node: TreeNode, e: MouseEvent) => {
  emit('node-click', data, node, e)
}

const handleChecked = (data: TreeNodeData, info: CheckedInfo) => {
  emit('check', data, info)
}

const setCurrentKey: LayoutTreeContext['setCurrentKey'] = (
  value: string | number
) => {
  nextTick(() => {
    tree.value?.setCurrentKey(value)
  })
}

const setDataList: LayoutTreeContext['setDataList'] = (data: any) => {
  if (props.lazy) {
    dataListData.value = data
  } else {
    dataListData.value = data
    tree.value?.setData(data)
  }
}

const setExpandedKeysData = (keys: any) => {
  if (!props.lazy) {
    tree.value?.setExpandedKeys(keys)
  }
}

const getExpanedKeys = (data: any) => {
  const flattenTreeData = flattenTree(data).map((item) => item[props.nodeKey])
  if (props.defaultObjectData) {
    flattenTreeData.unshift(props.defaultObjectData[props.nodeKey])
  } else {
    if (isTreeShowAll.value) {
      if (props.forceOnSelectAll) {
        flattenTreeData.unshift(
          props.queryTreeParams && props.queryTreeParams[props.lazyKey]
            ? props.queryTreeParams[props.lazyKey]
            : ''
        )
      }
    }
  }
  return flattenTreeData
}

const getTreeIds = (
  data: any,
  targetLevel: any,
  currentLevel = 1,
  ids: any[] = []
) => {
  if (currentLevel === targetLevel) {
    for (const node of data) {
      ids.push(node[props.nodeKey])
    }
  } else {
    for (const node of data) {
      if (node.children && node.children.length > 0) {
        getTreeIds(node.children, targetLevel, currentLevel + 1, ids)
      }
    }
  }
  return ids
}

const getTreeIds2 = (data: any[], currentLevel = 1, ids: any[] = []) => {
  for (const node of data) {
    ids.push(node[props.nodeKey])
    if (node.children && node.children.length > 0) {
      getTreeIds2(node.children, currentLevel + 1, ids)
    }
  }
  return ids
}

const handleExpand = () => {
  filterText.value = ''
  if (expandAllStatus.value) {
    const ids = getTreeIds2(dataListData.value)
    defaultExpandedKeys.value = ids
    setExpandedKeysData(defaultExpandedKeys.value)
  } else {
    const ids = getTreeIds(dataListData.value, props.expandedLevel)
    defaultExpandedKeys.value = ids
    setExpandedKeysData(defaultExpandedKeys.value)
  }
  expandAllStatus.value = !expandAllStatus.value
}

const query: LayoutTreeContext['query'] = () => getQueryTree()

onMounted(() => {
  nextTick(() => {
    height.value = elLayoutTreeBody.value.clientHeight
    if (props.isDefaultQuery) {
      if (props.dataList && props.dataList.length === 0) {
        getQueryTree()
      } else {
        setDataList(props.dataList)
        if (props.defaultExpandAll) {
          nextTick(() => {
            defaultExpandedKeys.value = getExpanedKeys(
              props.dataList
            ) as never[]
            setExpandedKeysData(defaultExpandedKeys.value)
          })
        } else {
          const ids = getTreeIds2(props.dataList, props.expandedLevel)
          nextTick(() => {
            defaultExpandedKeys.value = ids
            setExpandedKeysData(defaultExpandedKeys.value)
          })
        }
      }
    }
  })
})

defineExpose({
  /**
   * @description setCurrentKey
   */
  setCurrentKey,
  /**
   * @description getQueryTree
   */
  getQueryTree,
  /**
   * @description setDataList
   */
  setDataList,
  /**
   * @description query
   */
  query,
})
</script>
